/**
 * WIX CHANGE DETECTION HELPER
 * 
 * This helper provides aggressive change detection mechanisms for Wix environments
 * where Zone.js is broken and Angular's automatic change detection doesn't work.
 * 
 * Usage in modal components:
 * 1. Import and inject ChangeDetectorRef
 * 2. Call setupWixChangeDetection() in dialogRef.afterOpened()
 * 3. Call forceChangeDetection() after any user interaction
 */

import { ChangeDetectorRef } from '@angular/core';
import { timer, Subject, takeUntil } from 'rxjs';

export class WixChangeDetectionHelper {
  private static pollingIntervals = new Map<string, any>();
  private static destroy$ = new Subject<void>();

  /**
   * Sets up aggressive change detection for Wix environment
   * Call this in dialogRef.afterOpened() after style injection
   */
  static setupWixChangeDetection(
    cdRef: ChangeDetectorRef,
    componentId: string,
    options: {
      initialDelay?: number;
      pollInterval?: number;
      maxPolls?: number;
    } = {}
  ): void {
    const {
      initialDelay = 200,
      pollInterval = 100,
      maxPolls = 100 // Poll for ~10 seconds
    } = options;

    console.log(`🔧 [WIX FIX] Setting up change detection for component: ${componentId}`);

    // IMMEDIATE change detection
    try {
      cdRef.detectChanges();
      console.log(`✅ [WIX FIX] Immediate change detection triggered for ${componentId}`);
    } catch (err) {
      console.warn(`⚠️ [WIX FIX] Immediate detection failed for ${componentId}:`, err);
    }

    // Delayed change detection (original Wix fix)
    timer(initialDelay).subscribe(() => {
      try {
        cdRef.detectChanges();
        console.log(`✅ [WIX FIX] Delayed (${initialDelay}ms) change detection triggered for ${componentId}`);
      } catch (err) {
        console.warn(`⚠️ [WIX FIX] Delayed detection failed for ${componentId}:`, err);
      }
    });

    // AGGRESSIVE POLLING: Force detection periodically
    let pollCount = 0;
    const pollingTimer = setInterval(() => {
      pollCount++;
      
      try {
        cdRef.detectChanges();
        
        // Log occasionally
        if (pollCount % 20 === 0) {
          console.log(`🔄 [WIX FIX] Polling change detection #${pollCount} for ${componentId}`);
        }
      } catch (err) {
        // Silent fail
      }

      // Stop after max polls
      if (pollCount >= maxPolls) {
        clearInterval(pollingTimer);
        WixChangeDetectionHelper.pollingIntervals.delete(componentId);
        console.log(`⏹️ [WIX FIX] Stopped polling for ${componentId} after ${pollCount} polls`);
      }
    }, pollInterval);

    // Store interval for cleanup
    WixChangeDetectionHelper.pollingIntervals.set(componentId, pollingTimer);

    // Also use global force detection if available
    if (window['IMGoing']?.forceChangeDetection) {
      setTimeout(() => {
        window['IMGoing'].forceChangeDetection();
        console.log(`✅ [WIX FIX] Global force detection called for ${componentId}`);
      }, initialDelay + 50);
    }
  }

  /**
   * Force immediate change detection
   * Call this after user interactions (clicks, selections, etc.)
   */
  static forceChangeDetection(cdRef: ChangeDetectorRef, context: string = ''): void {
    try {
      // Angular change detection
      cdRef.detectChanges();
      
      // Also trigger global if available
      if (window['IMGoing']?.forceChangeDetection) {
        window['IMGoing'].forceChangeDetection();
      }
      
      if (context) {
        console.log(`✅ [WIX FIX] Force detection: ${context}`);
      }
    } catch (err) {
      console.warn(`⚠️ [WIX FIX] Force detection failed for ${context}:`, err);
    }
  }

  /**
   * Clean up polling for a component
   * Call this in ngOnDestroy
   */
  static cleanup(componentId: string): void {
    const interval = WixChangeDetectionHelper.pollingIntervals.get(componentId);
    if (interval) {
      clearInterval(interval);
      WixChangeDetectionHelper.pollingIntervals.delete(componentId);
      console.log(`🧹 [WIX FIX] Cleaned up polling for ${componentId}`);
    }
  }

  /**
   * Clean up all polling intervals
   */
  static cleanupAll(): void {
    WixChangeDetectionHelper.pollingIntervals.forEach((interval, id) => {
      clearInterval(interval);
      console.log(`🧹 [WIX FIX] Cleaned up polling for ${id}`);
    });
    WixChangeDetectionHelper.pollingIntervals.clear();
    WixChangeDetectionHelper.destroy$.next();
  }

  /**
   * Check if we're in a Wix environment
   */
  static isWixEnvironment(): boolean {
    return !!(
      window['__IMGOING_WIX_ENV__'] ||
      document.getElementById('imgoingcalendar-wrapper') ||
      document.querySelector('wix-default-custom-element')
    );
  }

  /**
   * Set up change detection for form controls
   * Call this for dropdowns, inputs, etc. that might not respond
   */
  static monitorFormControl(
    element: HTMLElement,
    cdRef: ChangeDetectorRef,
    context: string
  ): void {
    if (!WixChangeDetectionHelper.isWixEnvironment()) return;

    const events = ['click', 'change', 'input', 'focus'];
    
    events.forEach(eventType => {
      element.addEventListener(eventType, () => {
        WixChangeDetectionHelper.forceChangeDetection(cdRef, `${context} - ${eventType}`);
      }, true);
    });

    console.log(`👂 [WIX FIX] Monitoring ${events.join(', ')} on ${context}`);
  }
}
